import { INJECTIVE_CHAIN_ID } from './constants'
import { getWalletStrategy } from '@shared/wallet'
import { isEvmWallet } from '@injectivelabs/wallet-base'
import { ETHEREUM_CHAIN_ID } from '@shared/utils/constant'
import { EvmWalletStrategy } from '@injectivelabs/wallet-evm'
import { CosmosWalletStrategy } from '@injectivelabs/wallet-cosmos'
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom'
import { priorityMap, chainIdPriority } from './constants/asset-priority-map'
import type { Asset, Chain } from '@skip-go/client'
import type { Wallet } from '@injectivelabs/wallet-base'
import type { TokenStatic } from '@injectivelabs/sdk-ts'
import type { UiSkipTransaction, SkipAssetWithBalance } from '@/types/skip'

export const getAddress = async (chainId: string) => {
  // Ethereum mainnet
  if (parseInt(chainId)) {
    const walletStrategy = await getWalletStrategy()
    const wallet = walletStrategy.getWallet()

    const evmWalletStrategy = new EvmWalletStrategy({
      chainId: chainId as any,
      wallet: wallet as any,
      evmOptions: { evmChainId: chainId as any }
    })

    const provider = evmWalletStrategy.evmProviders[wallet]

    if (!provider) {
      const account = await evmWalletStrategy.getAddresses()
      if (account && account[0]) {
        return {
          chainId,
          address: account[0]
        }
      }

      throw new Error('Provider not found')
    }

    const accounts = (await provider.request({
      method: 'eth_requestAccounts'
    })) as string[]

    if (!accounts) throw new Error('Account not found')

    return {
      chainId,
      address: accounts?.[0]
    }
    // Solana mainnet
  } else if (chainId === 'solana') {
    const phantom = new PhantomWalletAdapter()
    await phantom.connect()
    const publicKey = phantom.publicKey?.toBase58()
    if (!publicKey) throw new Error('Unable to get Solana address')

    return {
      chainId,
      address: publicKey
    }
    // Cosmos chains
  } else {
    try {
      const walletStrategy = await getWalletStrategy()
      const wallet = walletStrategy.getWallet()

      const cosmosWalletStrategy = new CosmosWalletStrategy({
        chainId: chainId as any,
        wallet: wallet
      })

      const cosmosWallet = cosmosWalletStrategy.getCosmosWallet(chainId as any)

      const key = await cosmosWallet.getKey()

      if (!key) throw new Error(`No key for chainId: ${chainId}`)

      return {
        chainId,
        address: key.bech32Address
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_e: any) {
      return {
        chainId,
        address: ''
      }
      // throw new Error(`No key for chainId: ${chainId}`)
    }
  }
}

/**
 * Determines the chain type (evm or cosmos) based on the provided chainId.
 *
 * @param chainId The ID of the chain.
 * @returns The type of the chain ("evm" or "cosmos").
 */
export function getChainType(chainId: string | number): 'evm' | 'cosmos' {
  // EVM chain IDs are typically numeric and start from 1 (Ethereum Mainnet)
  if (
    typeof chainId === 'number' ||
    (typeof chainId === 'string' && !isNaN(Number(chainId)))
  ) {
    return 'evm'
  }

  // Cosmos chain IDs are typically in the format of "{chainName}-{networkId}"
  if (typeof chainId === 'string' && /^[a-zA-Z0-9-]+$/.test(chainId)) {
    return 'cosmos'
  }

  // If the chainId does not match any known format, throw an error
  throw new Error(`Unsupported chainId format: ${chainId}`)
}

export async function getCosmosAddressFromWallet({
  chainId,
  wallet
}: {
  wallet: Wallet
  chainId: string
}) {
  try {
    const cosmosWalletStrategy = new CosmosWalletStrategy({
      chainId: chainId as any,
      wallet: wallet
    })

    const cosmosWallet = cosmosWalletStrategy.getCosmosWallet(chainId as any)

    const key = await cosmosWallet.getKey()

    if (!key) throw new Error(`Noo key for chainId: ${chainId}`)

    return {
      chainId,
      address: key.bech32Address
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e: any) {
    return {
      chainId,
      address: ''
    }
  }
}

export async function getEvmAddressFromWallet({
  chainId,
  wallet
}: {
  wallet: Wallet
  chainId: string
}) {
  if (isNaN(parseInt(chainId))) {
    throw new Error('Invalid chainId')
  }

  if (!isEvmWallet(wallet)) {
    throw new Error('Invalid wallet')
  }

  const evmWalletStrategy = new EvmWalletStrategy({
    chainId: chainId as any,
    wallet: wallet as any,
    evmOptions: { evmChainId: chainId as any }
  })

  const provider = await evmWalletStrategy.getEip1193Provider()

  if (!provider) {
    throw new Error('Provider not found')
  }

  const accounts = await provider.request({
    method: 'eth_requestAccounts',
    params: []
  })

  if (!accounts) {
    throw new Error('Account not found')
  }

  return {
    chainId,
    address: accounts?.[0]
  }

  // const evmWalletStrategy = new EvmWalletStrategy({
  //   chainId: chainId as any,
  //   wallet: wallet as any,
  //   evmOptions: { evmChainId: chainId as any }
  // })

  // const provider = evmWalletStrategy.evmProviders[wallet]

  // if (!provider) {
  //   const account = await evmWalletStrategy.getAddresses()
  //   if (account && account[0]) {
  //     return {
  //       chainId,
  //       address: account[0]
  //     }
  //   }

  //   throw new Error('Provider not found')
  // }

  // const accounts = (await provider.request({
  //   method: 'eth_requestAccounts'
  // })) as string[]

  // if (!accounts) throw new Error('Account not found')

  // return {
  //   chainId,
  //   address: accounts?.[0]
  // }
}

export const peggyTokenToSkipAsset = (
  token: TokenStatic,
  isEvm: boolean
): Asset => {
  const denom = isEvm ? token.address.toLowerCase() : token.denom.toLowerCase()
  const originDenom = isEvm
    ? token.address.toLowerCase()
    : token.denom.toLowerCase()
  const chainId = isEvm ? String(ETHEREUM_CHAIN_ID) : INJECTIVE_CHAIN_ID
  const originChainId = isEvm ? String(ETHEREUM_CHAIN_ID) : INJECTIVE_CHAIN_ID

  return {
    denom,
    originDenom,
    chainId,
    originChainId,
    isEvm,
    isCw20: false,
    isSvm: false,
    trace: '',
    coingeckoId: token.coinGeckoId,
    decimals: token.decimals,
    description: '',
    logoUri: token.logo,
    name: token.name,
    recommendedSymbol: token.symbol,
    symbol: token.symbol,
    tokenContract: token.address
  }
}

export const sortChainsByPriorityFunction = (a: Chain, b: Chain) => {
  const aPriorityIndex = chainIdPriority.indexOf(a.chainId)
  const bPriorityIndex = chainIdPriority.indexOf(b.chainId)

  if (aPriorityIndex === -1 && bPriorityIndex !== -1) {
    return 1
  }

  if (bPriorityIndex === -1 && aPriorityIndex !== -1) {
    return -1
  }

  if (aPriorityIndex !== -1 && bPriorityIndex !== -1) {
    return aPriorityIndex - bPriorityIndex
  }

  return a.prettyName.localeCompare(b.prettyName)
}

export const sortAssetsByUsdAmountFunction = (
  a: SkipAssetWithBalance,
  b: SkipAssetWithBalance
) => {
  const aUsdBalance = parseFloat(a.balance?.valueUsd || '0')
  const bUsdBalance = parseFloat(b.balance?.valueUsd || '0')

  const aAmount = parseFloat(a.balance?.formattedAmount || '0')
  const bAmount = parseFloat(b.balance?.formattedAmount || '0')

  const aPriorityIndex =
    priorityMap[a.asset.chainId]
      ?.map((denom) => denom.toLowerCase())
      .indexOf(a.asset.denom.toLowerCase()) ?? -1

  const bPriorityIndex =
    priorityMap[b.asset.chainId]
      ?.map((denom) => denom.toLowerCase())
      .indexOf(b.asset.denom.toLowerCase()) ?? -1

  // 1. Sort by USD Balance (descending)
  if (aUsdBalance !== bUsdBalance) {
    return bUsdBalance - aUsdBalance
  }

  // 2. Sort by Amount (descending)
  if (aAmount !== bAmount) {
    return bAmount - aAmount
  }

  // 3. Sort by Priority Map (ascending priority index, -1 means not in map so goes last)
  if (aPriorityIndex !== bPriorityIndex) {
    // Assets in priority map come first
    if (aPriorityIndex === -1 && bPriorityIndex !== -1) {
      return 1
    }

    if (bPriorityIndex === -1 && aPriorityIndex !== -1) {
      return -1
    }
    // Both in priority map, sort by index
    if (aPriorityIndex !== -1 && bPriorityIndex !== -1) {
      return aPriorityIndex - bPriorityIndex
    }
  }

  return 0
}

export function getTxHashListFromSkipTransaction(
  skipTransactions: UiSkipTransaction
) {
  const txHastList: string[] = []

  if (!skipTransactions?.txData?.transferSequence?.length) {
    return txHastList
  }

  const firstStep = skipTransactions.txData.transferSequence[0]
  const lastStep =
    skipTransactions.txData.transferSequence[
      skipTransactions.txData.transferSequence.length - 1
    ]

  const firstStepTxHash = firstStep?.ibcTransfer?.packetTxs.sendTx?.txHash

  const lastStepTxHash = lastStep?.ibcTransfer?.packetTxs.receiveTx?.txHash

  if (firstStepTxHash) {
    txHastList.push(firstStepTxHash.toLowerCase())
  }

  if (lastStepTxHash) {
    txHastList.push(lastStepTxHash.toLowerCase())
  }

  return txHastList
}
